/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppConfig } from '../../../core/config';
import { ApiService } from '../../../core/services';
import { Session } from '../../simulator/models';
import { LabMetaData } from '../models';
import { SESSION_QUERY_KEYS } from './session-queries';

export interface UseCreateSessionProps {
  onSuccess?: VoidFunction;
}

export interface CreateSessionProps {
  labId: LabMetaData['id'] | null;
  instanceId?: string;
}

export const useCreateSession = ({ onSuccess }: UseCreateSessionProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ labId, instanceId = '' }: CreateSessionProps) => {
      const bodyRest =
        instanceId !== '' && ['development', 'integration', 'staging'].includes(AppConfig.env) ? { instanceId } : {};
      return ApiService.post<Session, any>(`v1/sessions/`, {
        labId,
        ...bodyRest,
        enableCenSol: true,
      });
    },
    onSuccess: (result, { labId }) => {
      queryClient.invalidateQueries({ queryKey: [SESSION_QUERY_KEYS.GET_ACTIVE_SESSION_BY_LAB_ID, { labId }] });
      if (onSuccess) {
        onSuccess();
      }
      window.open(`/session/${result.data.id}/simulator`, '_blank')?.focus();
    },
  });
};

export interface UseCloseSessionProps {
  onSuccess?: (sessionId: number) => void;
}

export interface CloseSessionProps {
  sessionId: number;
  labId: LabMetaData['id'] | null;
}

export const useCloseSession = ({ onSuccess }: UseCloseSessionProps) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId }: CloseSessionProps) => ApiService.put(`v1/sessions/${sessionId}/close`),
    onSuccess: (_, { sessionId, labId }) => {
      if (labId) {
        queryClient.invalidateQueries({ queryKey: [SESSION_QUERY_KEYS.GET_ACTIVE_SESSION_BY_LAB_ID, { labId }] });
      }
      if (onSuccess) {
        onSuccess(sessionId);
      }
    },
  });
};
